// ── 계약 등록 창 설정 — 칸 한 줄 + 스위치 (ContractFieldSettingsModal 에서 분리) ──
import React, { useState } from 'react';

/** 켜고 끄는 스위치. label 은 화면에 안 보이고 읽기 도구에만 쓰인다. */
export function Switch({ id, checked, onChange, label, disabled, mini }) {
  const w = mini ? 'w-[26px] h-[15px]' : 'w-8 h-[18px]';
  const knob = mini ? 'w-[11px] h-[11px]' : 'w-[14px] h-[14px]';
  const move = mini ? 'translate-x-[11px]' : 'translate-x-[14px]';
  return (
    <button
      type="button" id={id} role="switch" aria-checked={checked} aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${w} ${
        checked ? 'border-slate-900 bg-slate-900 dark:border-accent-600 dark:bg-accent-600' : 'border-[var(--border)] bg-[var(--border)]'
      }`}
    >
      <span
        className={`absolute left-[1px] top-[1px] rounded-full bg-white shadow transition-transform ${knob} ${checked ? move : ''}`}
      />
    </button>
  );
}

export function SettingsFieldRow({ meta, value, units, onChange }) {
  const on = Boolean(value.on);
  // "직접 쓰기"를 고른 상태를 따로 기억한다 (2026-09-23 버그 수정).
  //   예전에는 단위를 빈 값으로 만들어 표시했는데, 빈 값은 "설정 안 됨"과 구분이 안 돼
  //   고르자마자 '장'으로 되돌아가고 입력칸이 뜨지 않았다.
  const [typing, setTyping] = useState(() => Boolean(value.unit) && !units.includes(value.unit));
  const custom = typing || (Boolean(value.unit) && !units.includes(value.unit));

  return (
    <div className={`rounded-lg bg-[var(--bg-subtle)] px-2 py-1.5 ${on ? '' : 'opacity-50'}`}>
      <div className="flex items-center gap-2">
        <Switch
          id={`fld-${meta.id}`} checked={on} label={`${meta.name} 보이기`}
          onChange={(v) => onChange({ on: v })}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] text-[var(--text)]">{meta.name}</div>
          <div className="text-[10.5px] text-[var(--text-muted)]">
            {meta.kind}
            {meta.editOnlyNote && ' · 계약을 수정할 때만 나옵니다'}
          </div>
        </div>
        {!meta.noReq && (
          <button
            type="button" aria-pressed={Boolean(value.req)}
            onClick={() => onChange({ req: !value.req })}
            className={`shrink-0 rounded-md border px-2 py-[2px] text-[11px] font-bold ${
              value.req
                ? 'border-red-500 bg-red-500/10 text-red-500'
                : 'border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-muted)]'
            }`}
          >
            필수
          </button>
        )}
      </div>

      {on && (meta.hasUnit || meta.hasValidity) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-dashed border-[var(--border)] pt-1.5">
          {meta.hasUnit && (
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--text-sub)]">
              단위
              <select
                id={`unit-${meta.id}`} aria-label={`${meta.name} 단위`}
                value={custom ? '__free' : (value.unit || '장')}
                onChange={(e) => {
                  if (e.target.value === '__free') { setTyping(true); return; }  // 단위 값은 그대로 두고 칸만 연다
                  setTyping(false);
                  onChange({ unit: e.target.value });
                }}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-1.5 py-[2px] text-[11.5px] text-[var(--text)]"
              >
                {units.map((u) => <option key={u} value={u}>{u}</option>)}
                <option value="__free">직접 쓰기</option>
              </select>
              {custom && (
                <input
                  type="text" maxLength={4} placeholder="단위" autoFocus={typing}
                  aria-label={`${meta.name} 단위 직접 쓰기`}
                  value={value.unit || ''}
                  // 칸이 열리면 기존 글자를 선택해 둔다 — 바로 새 단위를 쳐서 덮어쓸 수 있게
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => onChange({ unit: e.target.value })}
                  className="w-[52px] rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-1.5 py-[2px] text-[11.5px] text-[var(--text)]"
                />
              )}
            </span>
          )}
          {meta.hasValidity && (
            <label className="inline-flex cursor-pointer items-center gap-1.5 text-[11.5px] text-[var(--text-sub)]">
              <Switch
                id={`val-${meta.id}`} checked={value.validity !== false} mini
                label={`${meta.name} 개월 칸 보이기`}
                onChange={(v) => onChange({ validity: v })}
              />
              개월 칸
            </label>
          )}
        </div>
      )}
    </div>
  );
}
